import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Printer, Download } from "lucide-react";
import html2pdf from "html2pdf.js";
import { Button } from "@/components/ui/button";
import { api, inr, fmtDate, amountInWords } from "@/lib/api";

const cell = "border border-slate-400 px-2 py-1.5 align-top";
const RCM_TEXT = "GST ON THIS INVOICE/FREIGHT IS PAYABLE BY THE RECIPIENT UNDER REVERSE CHARGE MECHANISM (RCM MODE).";

function RcmBanner({ b }) {
  if (!b.rcm) return null;
  return <div data-testid="print-rcm-banner" className="mt-3 border-2 border-slate-900 bg-amber-100 px-3 py-2 text-center text-xs font-extrabold tracking-wide">{"\u{1F449}"} {RCM_TEXT}</div>;
}

const stopsOf = (b) => ({
  pick: b.pickup_points?.length ? b.pickup_points : [{ city: b.pickup_location }],
  drop: b.drop_points?.length ? b.drop_points : [{ city: b.drop_location }],
});

function StopsTable({ b }) {
  const { pick, drop } = stopsOf(b);
  const rows = [...pick.map((s, i) => [`Loading Point ${i + 1}`, s]), ...drop.map((s, i) => [`Unloading Point ${i + 1}`, s])];
  return (
    <table className="mt-3 w-full border-collapse text-xs" data-testid="print-stops">
      <thead><tr className="bg-slate-100">{["Point", "Name", "Address", "City"].map((h) => <th key={h} className={`${cell} text-left`}>{h}</th>)}</tr></thead>
      <tbody>{rows.map(([l, s]) => <tr key={l}><td className={`${cell} font-semibold`}>{l}</td><td className={cell}>{s.name || "-"}</td><td className={cell}>{s.address || "-"}</td><td className={cell}>{s.city}</td></tr>)}</tbody>
    </table>
  );
}


function Header({ c, title, no, date }) {
  return (
    <div className="flex items-start justify-between border-b-2 border-slate-800 pb-3">
      <div>
        {c.logo_src && <img src={c.logo_src} alt="logo" className="mb-1 h-12 w-auto object-contain" data-testid="print-company-logo" />}
        <div className="text-2xl font-extrabold uppercase tracking-tight" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>{c.name}</div>
        <div className="text-xs">{c.address}{c.city ? `, ${c.city}` : ""}{c.state ? `, ${c.state}` : ""}</div>
        <div className="text-xs">Ph: {c.phone} · {c.email}</div>
        {c.gstin && <div className="text-xs font-semibold">GSTIN: {c.gstin}</div>}
        {c.pan && <div className="text-xs font-semibold" data-testid="print-company-pan">PAN: {c.pan}</div>}
        {c.bank_account && <div className="text-xs" data-testid="print-company-bank">Bank: {c.bank_name} · A/c {c.bank_account} · IFSC {c.bank_ifsc}{c.bank_branch ? ` · ${c.bank_branch}` : ""}</div>}
      </div>
      <div className="text-right">
        <div className="inline-block border-2 border-slate-800 px-3 py-1 text-sm font-bold uppercase">{title}</div>
        <div className="mt-2 text-xs">No: <b className="font-mono">{no}</b></div>
        <div className="text-xs">Date: <b>{fmtDate(date)}</b></div>
      </div>
    </div>
  );
}

function Parties({ b }) {
  return (
    <table className="mt-3 w-full border-collapse text-xs">
      <tbody>
        <tr>
          {["consignor", "consignee"].map((k) => (
            <td key={k} className={`${cell} w-1/2`}>
              <div className="font-bold uppercase text-[10px] text-slate-500">{k}</div>
              <div className="font-semibold">{b[k].name}</div>
              <div>{b[k].address}</div>
              {b[k].gstin && <div>GSTIN: {b[k].gstin}</div>}
              {b[k].phone && <div>Ph: {b[k].phone}</div>}
            </td>
          ))}
        </tr>
      </tbody>
    </table>
  );
}

function LR({ b, c }) {
  return (
    <>
      <Header c={c} title="Lorry Receipt / Bilty" no={b.lr_number} date={b.booking_date} />
      <table className="mt-3 w-full border-collapse text-xs">
        <tbody>
          <tr><td className={cell}><b>From:</b> {stopsOf(b).pick.map((s) => s.city).join(", ")}</td><td className={cell}><b>To:</b> {stopsOf(b).drop.map((s) => s.city).join(", ")}</td><td className={cell}><b>Vehicle:</b> {b.vehicle?.vehicle_number || "-"}</td></tr>
          <tr><td className={cell}><b>Driver:</b> {b.driver?.name || "-"} {b.driver?.phone && `(${b.driver.phone})`}</td><td className={cell}><b>E-Way Bill:</b> {b.eway_bill || "-"}</td><td className={cell}><b>GST Payable by:</b> <span className="capitalize">{b.rcm ? "Recipient (RCM)" : b.gst_paid_by}</span></td></tr>
          <tr><td className={cell} data-testid="print-loading-date"><b>Loading Date:</b> {b.loading_date ? fmtDate(b.loading_date) : "-"}</td><td className={cell} data-testid="print-unloading-date"><b>Unloading Date:</b> {b.unloading_date ? fmtDate(b.unloading_date) : "-"}</td><td className={cell}><b>ETA:</b> {b.eta ? fmtDate(b.eta) : "-"}</td></tr>
        </tbody>
      </table>
      <StopsTable b={b} />
      <Parties b={b} />
      <table className="mt-3 w-full border-collapse text-xs">
        <thead><tr className="bg-slate-100">{["Packages", "Description of Goods", "Weight (T)", "Goods Value", "Freight"].map((h) => <th key={h} className={`${cell} text-left`}>{h}</th>)}</tr></thead>
        <tbody>
          <tr className="h-20"><td className={cell}>{b.packages}</td><td className={cell}>{b.material_type}{b.remarks && <div className="mt-1 text-slate-500">{b.remarks}</div>}</td><td className={cell}>{b.weight_tons}</td><td className={cell}>{inr(b.invoice_value)}</td><td className={cell}>{b.show_freight_on_lr === false ? <span data-testid="lr-freight-fixed" className="font-bold">FIXED / AS PER AGREEMENT</span> : <>{inr(b.freight_amount, 2)}{b.freight_type === "per_mt" && <div className="text-slate-500">@ {inr(b.rate_per_mt)}/MT</div>}</>}</td></tr>
        </tbody>
      </table>
      {b.show_freight_on_lr === false ? (
        <div className="mt-3 flex justify-end">
          <table className="w-72 border-collapse text-xs">
            <tbody>
              <tr className="font-bold"><td className={cell}>Freight & Charges</td><td className={`${cell} text-right`} data-testid="lr-total-fixed">AS PER AGREEMENT / FIXED</td></tr>
            </tbody>
          </table>
        </div>
      ) : (
      <div className="mt-3 flex justify-end">
        <table className="w-72 border-collapse text-xs">
          <tbody>
            <tr><td className={cell}>Freight</td><td className={`${cell} text-right`}>{inr(b.freight_amount, 2)}</td></tr>
            {b.extra_total > 0 && <tr><td className={cell}>Extra Charges</td><td className={`${cell} text-right`}>{inr(b.extra_total, 2)}</td></tr>}
            <tr><td className={cell}>GST {b.rcm ? "(RCM)" : `(${b.gst_rate}%)`}</td><td className={`${cell} text-right`}>{b.rcm ? "Nil — RCM" : inr(b.gst_amount, 2)}</td></tr>
            <tr><td className={cell}>Advance Paid</td><td className={`${cell} text-right`}>{inr(b.advance_paid, 2)}</td></tr>
            <tr className="font-bold"><td className={cell}>Balance Payable</td><td className={`${cell} text-right`}>{inr(b.total_bill - b.advance_paid, 2)}</td></tr>
          </tbody>
        </table>
      </div>
      )}
      <RcmBanner b={b} />
      <div className="mt-12 grid grid-cols-3 gap-6 text-center text-xs">
        {["Consignor Signature", "Driver Signature", `For ${c.name}`].map((s) => <div key={s} className="border-t border-slate-500 pt-1">{s}</div>)}
      </div>
      <p className="mt-6 text-[10px] text-slate-500">Goods carried at owner's risk. Subject to {c.city || "local"} jurisdiction. Not responsible for leakage, breakage or damage due to natural causes.</p>
    </>
  );
}

const words = (n) => {
  const r = Math.floor(n || 0);
  const p = Math.round(((n || 0) - r) * 100);
  const w = (x) => amountInWords(x).replace(" Rupees Only", "");
  return `INR ${w(r)}${p ? ` and ${w(p)} paise` : ""} Only`;
};
const GST_STATES = { "01": "Jammu & Kashmir", "02": "Himachal Pradesh", "03": "Punjab", "04": "Chandigarh", "05": "Uttarakhand", "06": "Haryana", "07": "Delhi", "08": "Rajasthan", "09": "Uttar Pradesh", "10": "Bihar", "11": "Sikkim", "12": "Arunachal Pradesh", "13": "Nagaland", "14": "Manipur", "15": "Mizoram", "16": "Tripura", "17": "Meghalaya", "18": "Assam", "19": "West Bengal", "20": "Jharkhand", "21": "Odisha", "22": "Chhattisgarh", "23": "Madhya Pradesh", "24": "Gujarat", "26": "Dadra & Nagar Haveli and Daman & Diu", "27": "Maharashtra", "29": "Karnataka", "30": "Goa", "31": "Lakshadweep", "32": "Kerala", "33": "Tamil Nadu", "34": "Puducherry", "35": "Andaman & Nicobar", "36": "Telangana", "37": "Andhra Pradesh", "38": "Ladakh" };
const stateOf = (gstin, state) => {
  const code = (gstin || "").slice(0, 2);
  const name = state || GST_STATES[code];
  return name ? `${name}${code ? `, Code : ${code}` : ""}` : "-";
};
const bx = "border-slate-500";
const lbl = "text-[10px] text-slate-600";

function PartyBlock({ title, x }) {
  return (
    <div className={`border-b ${bx} px-1.5 py-1 last:border-b-0`}>
      <div className={lbl}>{title}</div>
      <div className="font-bold">{x.name || "-"}</div>
      {x.address && <div>{x.address}</div>}
      <div className="grid grid-cols-[80px_1fr]"><span>GSTIN/UIN</span><span>: {x.gstin || "-"}</span><span>State Name</span><span>: {stateOf(x.gstin, x.state)}</span></div>
    </div>
  );
}

function MetaCell({ k, v, span }) {
  return <div className={`border-b ${bx} px-1.5 py-0.5 ${span ? "col-span-2" : "odd:border-r"} min-h-[34px]`}><div className={lbl}>{k}</div><div className="font-bold">{v || ""}</div></div>;
}

function Invoice({ b, c }) {
  const buyer = b[b.billing_party] || b.consignor;
  const ship = b.consignee?.name ? b.consignee : buyer;
  const { pick, drop } = stopsOf(b);
  const taxable = b.taxable_value ?? b.freight_amount;
  const igst = b.gst_type ? b.gst_type === "igst" : (c.gstin || "").slice(0, 2) !== (buyer.gstin || "").slice(0, 2);
  const tax = Math.round(taxable * (b.gst_rate || 0)) / 100;
  const qty = b.weight_tons ? `${b.weight_tons} MT` : "";
  const items = [{ d: `${(b.material_type || "Goods").toUpperCase()} TRANSPORTATION${b.rcm ? ` RCM ${b.gst_rate}%` : ""}`, q: qty, rate: b.freight_type === "per_mt" ? b.rate_per_mt : "", per: b.freight_type === "per_mt" ? "MT" : "", amt: b.freight_amount },
    ...(b.extra_charges || []).map((x) => ({ d: `${x.label || x.type}${x.note ? ` — ${x.note}` : ""}`, amt: x.amount }))];
  if (!b.rcm && tax) items.push(...(igst ? [{ d: "IGST", rate: b.gst_rate, per: "%", amt: tax, tax: 1 }] : [{ d: "CGST", rate: b.gst_rate / 2, per: "%", amt: tax / 2, tax: 1 }, { d: "SGST", rate: b.gst_rate / 2, per: "%", amt: tax / 2, tax: 1 }]));
  if (b.claim_deduction > 0) items.push({ d: "Less: Damage / Shortage Deduction", amt: -b.claim_deduction });
  const n2 = (v) => Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const th = `border-b border-r ${bx} px-1 py-0.5 font-normal`;
  const td = `border-r ${bx} px-1 align-top`;
  return (
    <div className="text-[11px] leading-snug" data-testid="tally-invoice">
      <div className="mb-2 text-center text-base font-bold">Tax Invoice</div>
      <div className={`border ${bx}`}>
        <div className="grid grid-cols-2">
          <div className={`border-r ${bx}`}>
            <div className={`border-b ${bx} px-1.5 py-1`}>
              {c.logo_src && <img src={c.logo_src} alt="logo" className="mb-1 h-10 w-auto object-contain" data-testid="print-company-logo" />}
              <div className="font-bold">{c.name}</div>
              <div>{[c.address, c.city, c.state].filter(Boolean).join(", ")}</div>
              <div>GSTIN/UIN: {c.gstin || "-"}</div>
              <div>State Name : {stateOf(c.gstin, c.state)}</div>
              {c.email && <div>E-Mail : {c.email}</div>}
            </div>
            <PartyBlock title="Consignee (Ship to)" x={ship} />
            <PartyBlock title="Buyer (Bill to)" x={buyer} />
          </div>
          <div className="grid grid-cols-2 content-start">
            <MetaCell k="Invoice No." v={b.invoice_number} /><MetaCell k="Dated" v={fmtDate(b.bill_created_at || b.delivered_at || b.booking_date)} />
            <MetaCell k="Delivery Note" /><MetaCell k="Mode/Terms of Payment" />
            <MetaCell k="Reference No. & Date." v={b.party_booking_no} /><MetaCell k="Other References" v={b.eway_bill ? `E-Way Bill: ${b.eway_bill}` : ""} />
            <MetaCell k="Buyer's Order No." /><MetaCell k="Dated" />
            <MetaCell k="Dispatch Doc No." /><MetaCell k="Delivery Note Date" v={b.delivered_at ? fmtDate(b.delivered_at) : ""} />
            <MetaCell k="Dispatched through" v={pick.map((s) => s.city).join(", ")} /><MetaCell k="Destination" v={drop.map((s) => s.city).join(", ")} />
            <MetaCell k="Bill of Lading/LR-RR No." v={`${b.lr_number || ""} dt. ${fmtDate(b.booking_date)}`} /><MetaCell k="Motor Vehicle No." v={b.vehicle?.vehicle_number || b.market_vehicle_number} />
            <div className="col-span-2 min-h-[60px] px-1.5 py-0.5"><div className={lbl}>Terms of Delivery</div></div>
          </div>
        </div>
        <table className={`w-full border-collapse border-t ${bx}`}>
          <thead><tr>{["Sl No.", "Description of Services", "HSN/SAC", "Quantity", "Rate", "per", "Amount"].map((h) => <th key={h} className={`${th} ${h === "Amount" ? "border-r-0" : ""}`}>{h}</th>)}</tr></thead>
          <tbody>
            {items.map((x, i) => (
              <tr key={i} data-testid={`invoice-row-${i}`}>
                <td className={td}>{x.tax ? "" : i + 1}</td>
                <td className={`${td} ${x.tax ? "text-right italic" : "font-bold"}`}>{x.d}</td>
                <td className={td}>{x.tax ? "" : "996511"}</td>
                <td className={`${td} text-right font-bold`}>{x.q || ""}</td>
                <td className={`${td} text-right`}>{x.rate !== "" && x.rate != null ? n2(x.rate) : ""}</td>
                <td className={td}>{x.per || ""}</td>
                <td className="px-1 text-right align-top font-bold">{n2(x.amt)}</td>
              </tr>
            ))}
            <tr className="h-40"><td className={td} /><td className={td} /><td className={td} /><td className={td} /><td className={td} /><td className={td} /><td /></tr>
            <tr className={`border-t ${bx}`}><td className={td} /><td className={`${td} text-right`}>Total</td><td className={td} /><td className={`${td} text-right font-bold`}>{qty}</td><td className={td} /><td className={td} /><td className="px-1 text-right text-sm font-bold" data-testid="invoice-grand-total">₹ {n2(b.total_bill)}</td></tr>
          </tbody>
        </table>
        <div className={`flex justify-between border-t ${bx} px-1.5 py-1`}><div><div className={lbl}>Amount Chargeable (in words)</div><div className="font-bold">{words(b.total_bill)}</div></div><div className="italic">E. & O.E</div></div>
        <table className={`w-full border-collapse border-t ${bx} text-right`} data-testid="invoice-tax-table">
          <thead>
            <tr><th className={th} rowSpan={2} /><th className={th} rowSpan={2}>Taxable Value</th>{(igst ? ["Integrated Tax"] : ["Central Tax", "State Tax"]).map((h) => <th key={h} className={th} colSpan={2}>{h}</th>)}<th className={`${th} border-r-0`} rowSpan={2}>Total Tax Amount</th></tr>
            <tr>{(igst ? [1] : [1, 2]).map((k) => [<th key={`r${k}`} className={th}>Rate</th>, <th key={`a${k}`} className={th}>Amount</th>])}</tr>
          </thead>
          <tbody>
            <tr className="font-bold"><td className={`${td} text-left`}>Total:</td><td className={td}>{n2(taxable)}</td>
              {(igst ? [b.gst_rate] : [b.gst_rate / 2, b.gst_rate / 2]).map((r, k) => [<td key={`r${k}`} className={td}>{r}%</td>, <td key={`a${k}`} className={td}>{n2(igst ? tax : tax / 2)}</td>])}
              <td className="px-1">{n2(tax)}</td></tr>
          </tbody>
        </table>
        <div className={`border-t ${bx} px-1.5 py-1`}>
          <div>Tax Amount (in words) : <b className="text-xs">{words(tax)}</b></div>
          <div data-testid="invoice-rcm-line">Amount of tax subject to Reverse Charge{b.rcm ? <b> : ₹ {n2(tax)} (payable by recipient under RCM)</b> : " : Nil"}</div>
          {b.pod_status === "not_ok" && <div data-testid="print-pod-note" className="mt-1 font-bold text-rose-700">POD RECEIVED WITH ISSUES — {b.shortage_qty > 0 && `Shortage: ${b.shortage_qty}. `}{b.damage_desc || b.shortage_desc}</div>}
        </div>
        <div className="grid grid-cols-2">
          <div className={`flex flex-col justify-end border-r ${bx} px-1.5 py-1`}><div>Declaration</div><div>We declare that this invoice shows the actual price of the services described and that all particulars are true and correct.</div></div>
          <div>
            <div className="px-1.5 py-1" data-testid="print-company-bank">
              <div>Company's Bank Details</div>
              <div className="grid grid-cols-[110px_1fr]"><span>Bank Name</span><b>: {c.bank_name || "-"}</b><span>A/c No.</span><b>: {c.bank_account || "-"}</b><span>Branch & IFS Code</span><b>: {[c.bank_branch, c.bank_ifsc].filter(Boolean).join(" & ") || "-"}</b></div>
            </div>
            <div className={`flex h-20 flex-col justify-between border-t ${bx} px-1.5 py-1 text-right`}><b>for {c.name}</b><span>Authorised Signatory</span></div>
          </div>
        </div>
      </div>
      <div className="mt-1 text-center">This is a Computer Generated Invoice</div>
    </div>
  );
}

function TripSheet({ b, c }) {
  const km = (v) => (v == null ? "-" : `${Number(v).toLocaleString("en-IN")} km`);
  const exp = (b.expenses || []).filter((e) => e.status !== "rejected");
  return (
    <>
      <Header c={c} title="Trip Sheet" no={b.lr_number} date={b.booking_date} />
      <table className="mt-3 w-full border-collapse text-xs">
        <tbody>
          <tr><td className={cell}><b>Vehicle:</b> {b.vehicle?.vehicle_number || "-"}</td><td className={cell}><b>Driver:</b> {b.driver?.name || "-"} {b.driver?.phone && `(${b.driver.phone})`}</td><td className={cell}><b>Status:</b> {({ "In-Transit": "Delivery", Delivered: "Loading Point" })[b.status] || b.status}</td></tr>
          <tr><td className={cell}><b>Material:</b> {b.material_type}</td><td className={cell}><b>Weight:</b> {b.weight_tons} MT</td><td className={cell}><b>Packages:</b> {b.packages}</td></tr>
          <tr><td className={cell} data-testid="print-loading-date"><b>Loading Date:</b> {b.loading_date ? fmtDate(b.loading_date) : "-"}</td><td className={cell} data-testid="print-unloading-date"><b>Unloading Date:</b> {b.unloading_date ? fmtDate(b.unloading_date) : "-"}</td><td className={cell}><b>ETA:</b> {b.eta ? fmtDate(b.eta) : "-"}</td></tr>
        </tbody>
      </table>
      <StopsTable b={b} />
      <div className="mt-3 font-bold text-xs uppercase">Trip Summary — Odometer</div>
      <table className="mt-1 w-full border-collapse text-xs" data-testid="print-odometer">
        <thead><tr className="bg-slate-100">{["Start KM", "End KM", "Total Distance Run"].map((h) => <th key={h} className={`${cell} text-left`}>{h}</th>)}</tr></thead>
        <tbody><tr><td className={cell}>{km(b.trip_start_km)}</td><td className={cell}>{km(b.trip_end_km)}</td><td className={`${cell} font-bold`}>{km(b.distance_km)}</td></tr></tbody>
      </table>
      <div className="mt-3 font-bold text-xs uppercase">Delivery & POD</div>
      <table className="mt-1 w-full border-collapse text-xs" data-testid="print-pod-status">
        <tbody>
          <tr><td className={cell}><b>POD Status:</b> {b.pod_status === "ok" ? "POD OK" : b.pod_status === "not_ok" ? "POD NOT OK" : "Pending"}</td>
              <td className={cell}><b>Shortage:</b> {b.shortage_qty ? `${b.shortage_qty} — ${b.shortage_desc || ""}` : "Nil"}</td>
              <td className={cell}><b>Damage:</b> {b.damage_desc || "Nil"}</td>
              <td className={cell}><b>Claim/Deduction:</b> {b.claim_amount ? inr(b.claim_amount) + (b.claim_deduction > 0 ? " (deducted)" : "") : "Nil"}</td></tr>
        </tbody>
      </table>
      <div className="mt-3 font-bold text-xs uppercase">Trip Expenses</div>
      <table className="mt-1 w-full border-collapse text-xs">
        <thead><tr className="bg-slate-100">{["Date", "Type", "Note", "Amount"].map((h) => <th key={h} className={`${cell} text-left`}>{h}</th>)}</tr></thead>
        <tbody>
          {exp.map((e) => <tr key={e.id}><td className={cell}>{fmtDate(e.date)}</td><td className={`${cell} capitalize`}>{e.type}{e.litres ? ` (${e.litres} L)` : ""}</td><td className={cell}>{e.note || "-"}</td><td className={`${cell} text-right`}>{inr(e.amount)}</td></tr>)}
          <tr className="font-bold"><td className={cell} colSpan={3}>Total</td><td className={`${cell} text-right`}>{inr(exp.reduce((s, e) => s + e.amount, 0))}</td></tr>
        </tbody>
      </table>
      <div className="mt-14 grid grid-cols-2 gap-10 text-center text-xs">
        {["Driver Signature", `For ${c.name}`].map((s) => <div key={s} className="border-t border-slate-500 pt-1">{s}</div>)}
      </div>
    </>
  );
}

export default function PrintDoc() {
  const { kind, id } = useParams();
  const navigate = useNavigate();
  const ref = useRef();
  const [b, setB] = useState(null);
  const [c, setC] = useState(null);
  useEffect(() => { api.get(`/bookings/${id}`).then((r) => setB(r.data)); api.get("/company").then(async (r) => {
    const co = r.data;
    if (co.logo_file_id) {
      try { const lg = await api.get(`/files/${co.logo_file_id}`, { responseType: "blob" }); co.logo_src = URL.createObjectURL(lg.data); } catch { /* no logo */ }
    }
    setC(co);
  }); }, [id]);
  if (!b || !c) return <div className="p-8 text-sm text-muted-foreground">Preparing document…</div>;
  const name = kind === "lr" ? b.lr_number : kind === "tripsheet" ? `TRIP-${b.lr_number}` : b.invoice_number;
  const download = () => html2pdf().set({ margin: 8, filename: `${name}.pdf`, html2canvas: { scale: 2 }, jsPDF: { format: "a4" } }).from(ref.current).save();

  return (
    <div className="min-h-screen bg-secondary/60 py-6">
      <div className="no-print mx-auto mb-4 flex max-w-[820px] flex-wrap items-center justify-between gap-2 px-4">
        <Button variant="outline" data-testid="print-back-btn" onClick={() => navigate(-1)}><ArrowLeft className="mr-1 h-4 w-4" />Back</Button>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="print-btn" onClick={() => window.print()}><Printer className="mr-1 h-4 w-4" />Print</Button>
          <Button data-testid="download-pdf-btn" onClick={download}><Download className="mr-1 h-4 w-4" />Download PDF</Button>
        </div>
      </div>
      <div className="mx-auto max-w-[820px] overflow-x-auto px-4">
        <div ref={ref} data-testid={`print-${kind}-doc`} className="min-w-[700px] bg-white p-8 text-slate-900 shadow-sm" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>
          {kind === "lr" ? <LR b={b} c={c} /> : kind === "tripsheet" ? <TripSheet b={b} c={c} /> : <Invoice b={b} c={c} />}
        </div>
      </div>
    </div>
  );
}
